import React, { useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { withSize } from 'react-sizeme';
import classNames from 'classnames';

import {
    Sizes,
    Colours,
    Typography,
    MessagePropType,
    PrimaryAnchor,
    PageBehaviourButton,
    AnimatedOpacityContainer,
    createRibbonMixin,
    IconMinus,
    IconPlus,
    useSmoothCollapse,
    ANALYTICS_KEY,
    ANALYTICS_ATTRIBUTE_NAME,
} from '@ratehub/base-ui';

import ProductTableColumnPropType from '../definitions/ProductTableColumnPropType';
import ProductTableRowPropType from '../definitions/ProductTableRowPropType';


const MESSAGES = defineMessages({
    labelIsExpanded: {
        id: 'web-components.ResponsiveTable.labelIsExpanded',
        defaultMessage: '{isExpanded, select, true {Show less} other {Show more}}',
    },
    noRowDataMessage: {
        id: 'web-components.ResponsiveTable.noRowDataMessage',
        defaultMessage: 'No row data available',
    },
});

function ResponsiveTable({
    size,

    columns,
    rows,

    maxRowsCollapsed,
    maxRowsExpanded,
    isAlwaysExpanded,

    footerCtaMessage,
    footerCtaHref,

    noRowDataMessage,
    isToggleButtonWithinTable,

    ...otherProps
}) {
    const intl = useIntl();
    const containerRef = useRef(null);
    const [ isExpanded, toggleExpanded ] = useSmoothCollapse({
        containerRef,
        controlledIsExpanded: isAlwaysExpanded,
    });

    const width = size.width
        ? Math.floor(size.width) // use an integer value for columnar show/hide comparisons
        : undefined; // when on the server, assume a large desktop for full rendering
    const isServerRendered = typeof size.width === 'undefined';
    const isCompact = !isServerRendered && width < MAX_COMPACT_WIDTH; // never isCompact on server render

    const hasInlineColumns = columns.some(column => !isFullRow(column, width));
    const hasFullWidthColumns = columns.some(column => isFullRow(column, width));

    const hasFooterCTA = footerCtaMessage && footerCtaHref;

    // render up to maxRowsExpanded rows in the DOM (even if hidden with display: none)
    rows = rows.slice(0, maxRowsExpanded);

    const maxRows = isExpanded ? maxRowsExpanded : maxRowsCollapsed;

    let visibleColumnCount = columns.reduce((acc, column) => {
        if (!isContentHidden(column, width) && !isFullRow(column, width)) {
            ++acc;
        }

        return acc;
    }, 0);

    if (visibleColumnCount === 0) {
        visibleColumnCount = undefined;
    }


    // - isServerRendered is true on the server AND for initial renders in the browser
    // - when it becomes false, we have size.width and will fade-in the opacity container
    return (
        <AnimatedOpacityContainer isOpaque={!isServerRendered}>
            <ResponsiveTable.Container
                size={size}
                isCompact={isCompact}
                ref={containerRef}
                {...otherProps}
            >
                <div className="table-container">
                    <Choose>
                        <When condition={rows.length}>
                            <table>
                                <thead>
                                    <tr>
                                        <For
                                            each="column"
                                            of={columns}
                                            index="index"
                                        >
                                            <If condition={!isFullRow(column, width)}>
                                                <th
                                                    key={column.label || index}
                                                    className={classNames(
                                                        { [`align-${column.align}`]: true },
                                                        { hidden: width < column.hideBelowWidth },
                                                    )}
                                                >
                                                    {column.label}
                                                </th>
                                            </If>
                                        </For>
                                    </tr>
                                </thead>

                                <tbody>
                                    <For
                                        each="row"
                                        of={rows}
                                        index="rowIndex"
                                    >
                                        <If condition={hasInlineColumns}>
                                            <tr
                                                key={rowIndex}
                                                style={
                                                    row.backgroundColour
                                                        ? { backgroundColor: row.backgroundColour }
                                                        : null
                                                }
                                                className={classNames(
                                                    { hideBorder: rowIndex === maxRows - 1 || hasFullWidthColumns },
                                                    { hidden: rowIndex >= maxRows },
                                                    ANALYTICS_ATTRIBUTE_NAME,
                                                )}
                                                ref={self => {
                                                    if (self) {
                                                        self[ANALYTICS_KEY] = row.analyticsObject;
                                                    }
                                                }}
                                            >
                                                <For
                                                    each="column"
                                                    of={columns}
                                                    index="colIndex"
                                                >
                                                    <If
                                                        condition={
                                                            !isFullRow(column, width)
                                                            && !(isServerRendered && column.hideAboveOrEqualWidth)
                                                            // will not server-side render anything with a hideAbove value
                                                            // (which targets columns reserved for "mobile" displays)
                                                        }
                                                    >
                                                        <td
                                                            key={`${rowIndex} - ${colIndex}`}
                                                            className={classNames(
                                                                { [`align-${column.align}`]: true },
                                                                { hidden: isContentHidden(column, width) },
                                                                row.htmlCellContents?.[colIndex]?.wrapperClassName,
                                                            )}
                                                            data-ribbon-message={row.ribbonMessage}
                                                        >
                                                            {row.htmlCellContents?.[colIndex]?.content}
                                                        </td>
                                                    </If>
                                                </For>
                                            </tr>
                                        </If>

                                        {/* only full-width td’s below here */}
                                        <If condition={hasFullWidthColumns}>
                                            <For
                                                each="column"
                                                of={columns}
                                                index="colIndex"
                                            >
                                                <If
                                                    condition={
                                                        isFullRow(column, width)
                                                        && !(isServerRendered && column.hideAboveOrEqualWidth)
                                                    }
                                                >
                                                    <tr
                                                        key={colIndex}
                                                        className={classNames(
                                                            {
                                                                hideBorder:
                                                                    rowIndex === maxRows - 1
                                                                    || colIndex !== columns.length - 1,
                                                            },
                                                            { hidden: rowIndex >= maxRows },
                                                            ANALYTICS_ATTRIBUTE_NAME,
                                                        )}
                                                        ref={self => {
                                                            if (self) {
                                                                self[ANALYTICS_KEY] = row.analyticsObject;
                                                            }
                                                        }}
                                                    >
                                                        <td
                                                            key={`${rowIndex} - ${colIndex}`}
                                                            className={classNames(
                                                                { [`align-${column.align}`]: true },
                                                                { hidden: isContentHidden(column, width) },
                                                                { fullWidth: true },
                                                                row.htmlCellContents?.[colIndex]?.wrapperClassName,
                                                            )}
                                                            colSpan={visibleColumnCount}
                                                        >
                                                            {row.htmlCellContents?.[colIndex]?.content}
                                                        </td>
                                                    </tr>
                                                </If>
                                            </For>
                                        </If>
                                    </For>
                                </tbody>
                            </table>
                        </When>
                        <Otherwise>
                            <p className="rh-text-m rh-py-5 no-row-data-message">
                                {intl.formatMessage(noRowDataMessage)}
                            </p>
                        </Otherwise>
                    </Choose>

                    <Choose>
                        <When condition={hasFooterCTA}>
                            <div className="cta-container rh-display-flex">
                                <PrimaryAnchor
                                    className="cta-button"
                                    data-name="responsiveTable-cta"
                                    message={footerCtaMessage}
                                    href={footerCtaHref}
                                    size="medium"
                                />
                            </div>
                        </When>
                        <When condition={!isAlwaysExpanded && isToggleButtonWithinTable}>
                            <div className="cta-container rh-display-flex">
                                <PageBehaviourButton
                                    className="rh-my-1_25 rh-mx-auto"
                                    onClick={toggleExpanded}
                                    message={intl.formatMessage(MESSAGES.labelIsExpanded, { isExpanded: isExpanded })}
                                    data-name="responsive-table-expand"
                                    size="small"
                                    iconLocation="left"
                                    iconComponent={isExpanded ? IconMinus : IconPlus}
                                />
                            </div>
                        </When>
                    </Choose>
                </div>

                <If condition={!isAlwaysExpanded && !isToggleButtonWithinTable}>
                    <div className="rh-py-0_75 rh-pl-0_75">
                        <PageBehaviourButton
                            className="rh-ml-auto"
                            onClick={toggleExpanded}
                            message={intl.formatMessage(MESSAGES.labelIsExpanded, { isExpanded: isExpanded })}
                            data-name="responsive-table-expand"
                            size="medium"
                        />
                    </div>
                </If>
            </ResponsiveTable.Container>
        </AnimatedOpacityContainer>
    );
}

ResponsiveTable.propTypes = {
    size: PropTypes.shape({
        width: PropTypes.number,
    }).isRequired,
    rows: ProductTableRowPropType.isRequired,
    columns: ProductTableColumnPropType.isRequired,
    maxRowsCollapsed: PropTypes.number,
    maxRowsExpanded: PropTypes.number,
    footerCtaMessage: MessagePropType,
    footerCtaHref: PropTypes.string,
    noRowDataMessage: MessagePropType,
    isAlwaysExpanded: PropTypes.bool,
    isToggleButtonWithinTable: PropTypes.bool,
};

ResponsiveTable.defaultProps = {
    maxRowsCollapsed: 4,
    maxRowsExpanded: 10,
    footerCtaMessage: undefined,
    footerCtaHref: undefined,
    noRowDataMessage: MESSAGES.noRowDataMessage,
    isAlwaysExpanded: false,
    isToggleButtonWithinTable: undefined,
};

const MAX_COMPACT_WIDTH = 700; // px

function isFullRow(column, currentWidth) {
    return column.isAlwaysFullRow || currentWidth < column.fullRowBelowWidth;
}

// returns true if container width is outside their respective lower or upper limits
function isContentHidden(column, currentWidth) {
    return currentWidth < column.hideBelowWidth || currentWidth >= column.hideAboveOrEqualWidth;
}

ResponsiveTable.Container = styled.div`
    background-color: ${Colours.COCONUT};

    .table-container {
        border: 1px solid ${Colours.STONE};

        padding-left: ${({ isCompact }) => (isCompact ? Sizes.SPACING.THREE_QUARTERS : Sizes.SPACING.ONE_AND_A_HALF)};
        padding-right: ${({ isCompact }) => (isCompact ? Sizes.SPACING.THREE_QUARTERS : Sizes.SPACING.ONE_AND_A_HALF)};

        /* Fix for tables that are too horizontally large to fit on a mobile screen */
        overflow-x: ${({ isCompact }) => isCompact ? 'auto' : 'visible'};

        > table {
            width: 100%;
            font-family: ${Typography.FONT_FAMILY_STACK};
            border-radius: ${Sizes.SPACING.EIGHTH};
            border-collapse: collapse;

            > thead {
                > tr {
                    border-bottom: 1px solid ${Colours.STONE};

                    > th {
                        &.hidden {
                            display: none;
                        }

                        &.align-left {
                            text-align: left;
                        }

                        &.align-center {
                            text-align: center;
                        }

                        &.align-right {
                            text-align: right;
                        }

                        padding-top: ${Sizes.SPACING.HALF};
                        padding-bottom: ${Sizes.SPACING.HALF};

                        color: ${Colours.STONE_DARKEST};

                        font-size: ${Sizes.FONTS['2XS']};
                        font-weight: ${Typography.WEIGHTS.REGULAR};
                    }
                }
            }

            > tbody {
                > tr > td[data-ribbon-message]:first-child {
                    ${props =>
        createRibbonMixin(
            Sizes.SPACING.HALF,
            `-${props.isCompact ? Sizes.SPACING.THREE_QUARTERS : Sizes.SPACING.ONE_AND_A_HALF}`,
        )};

                    &::before {
                        content: attr(data-ribbon-message);
                    }
                }

                > tr {
                    border-bottom: 1px solid ${Colours.STONE};

                    &.hidden {
                        display: none;
                    }

                    &.hideBorder {
                        border-bottom-width: 0;
                    }

                    > td {
                        &.hidden {
                            display: none;
                        }

                        &.align-left {
                            text-align: left;
                        }

                        &.align-center {
                            text-align: center;
                        }

                        &.align-right {
                            text-align: right;
                        }

                        &.fullWidth {
                            padding-top: 0;
                        }

                        padding-top: ${({ isCompact }) =>
        isCompact ? Sizes.SPACING.ONE_AND_A_HALF : Sizes.SPACING.TWO};
                        padding-bottom: ${({ isCompact }) =>
        isCompact ? Sizes.SPACING.ONE_AND_A_HALF : Sizes.SPACING.TWO};

                        font-size: ${({ isCompact }) => (isCompact ? Sizes.FONTS['2XS'] : Sizes.FONTS.S)};

                        /* BELOW: Some handy utility classes you can use on your
                        cell contents for common styling across all tables.
                        Would like to refactor these out of here, see:
                        https://ratehub.atlassian.net/browse/FIT-1778 */

                        > .rate {
                            display: flex;
                            flex-direction: column;

                            > .featured {
                                text-transform: lowercase;
                                font-size: ${Sizes.FONTS['2XS']};
                                line-height: 1.75;
                            }

                            > .large {
                                font-size: ${Sizes.FONTS['3XL']};
                            }
                        }
                    }
                }
            }

            th,
            td {
                padding-left: ${({ isCompact }) => (isCompact ? Sizes.SPACING.THREE_QUARTERS : Sizes.SPACING.ONE)};
                padding-right: ${({ isCompact }) => (isCompact ? Sizes.SPACING.THREE_QUARTERS : Sizes.SPACING.ONE)};
            }
        }

        > .cta-container {
            border-top: 1px solid ${Colours.STONE};
            text-align: center;

            > .cta-button {
                width: auto;
                display: inline-block;
                margin: ${Sizes.SPACING.ONE_AND_A_HALF} auto;
            }
        }

        > .no-row-data-message {
            text-align: center;
        }
    }
`;

export default withSize({ noPlaceholder: true })(ResponsiveTable);
